import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  DoctorAccountControlDoctorInbox,
  DoctorAccountControlDoctorSummary,
  DoctorAccountControlGrantSetting,
  DoctorAccountControlRequest,
} from '@jema-manager/shared';
import { doctorAccountControlApi } from '@shared/api/doctor-account-control';

export const doctorAccountControlQueryKeys = {
  all: ['doctor-account-control'] as const,
  doctorSummary: (doctorId: string) =>
    [...doctorAccountControlQueryKeys.all, 'doctor-summary', doctorId] as const,
  doctorInbox: () => [...doctorAccountControlQueryKeys.all, 'doctor-inbox'] as const,
  standingGrant: () => [...doctorAccountControlQueryKeys.all, 'standing-grant'] as const,
  approvalDetail: (token: string) =>
    [...doctorAccountControlQueryKeys.all, 'approval-detail', token] as const,
};

const syncDoctorControlCaches = (
  queryClient: QueryClient,
  request: DoctorAccountControlRequest
) => {
  queryClient.setQueryData<DoctorAccountControlDoctorSummary | undefined>(
    doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
    current => ({
      ...current,
      openRequest:
        request.status === 'ended' || request.status === 'revoked' || request.status === 'denied'
          ? null
          : request,
      activeSession: request.status === 'active' ? request : null,
      standingGrant: current?.standingGrant ?? null,
    })
  );

  queryClient.setQueryData<DoctorAccountControlDoctorInbox | undefined>(
    doctorAccountControlQueryKeys.doctorInbox(),
    current => ({
      ...current,
      pendingRequest: request.status === 'requested' ? request : null,
      activeSession: request.status === 'active' ? request : null,
      standingGrant: current?.standingGrant ?? null,
    })
  );
};

export const useDoctorAccountControlDoctorSummary = (
  doctorId: string | null | undefined,
  enabled = true
) =>
  useQuery({
    queryKey: doctorId ? doctorAccountControlQueryKeys.doctorSummary(doctorId) : [],
    queryFn: () => {
      if (!doctorId) {
        throw new Error('Doctor id is required');
      }
      return doctorAccountControlApi.getDoctorSummary(doctorId);
    },
    enabled: Boolean(doctorId && enabled),
    staleTime: 2_000,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  });

export const useDoctorAccountControlDoctorInbox = (enabled = true) =>
  useQuery({
    queryKey: doctorAccountControlQueryKeys.doctorInbox(),
    queryFn: () => doctorAccountControlApi.getMyDoctorInbox(),
    enabled,
    staleTime: 2_000,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  });

export const useDoctorAccountControlStandingGrant = (enabled = true) =>
  useQuery({
    queryKey: doctorAccountControlQueryKeys.standingGrant(),
    queryFn: () => doctorAccountControlApi.getMyStandingGrant(),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  });

export const useUpdateDoctorAccountControlStandingGrant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorAccountControlApi.updateMyStandingGrant,
    onSuccess: grant => {
      queryClient.setQueryData<DoctorAccountControlGrantSetting>(
        doctorAccountControlQueryKeys.standingGrant(),
        grant
      );
      queryClient.setQueryData<DoctorAccountControlDoctorInbox | undefined>(
        doctorAccountControlQueryKeys.doctorInbox(),
        current => ({
          ...current,
          pendingRequest: current?.pendingRequest ?? null,
          activeSession: current?.activeSession ?? null,
          standingGrant: grant,
        })
      );
    },
  });
};

export const useDoctorAccountControlApprovalDetail = (
  token: string | null | undefined,
  enabled = true
) =>
  useQuery({
    queryKey: token ? doctorAccountControlQueryKeys.approvalDetail(token) : [],
    queryFn: () => {
      if (!token) {
        throw new Error('Approval token is required');
      }
      return doctorAccountControlApi.getApprovalDetail(token);
    },
    enabled: Boolean(token && enabled),
    retry: false,
    staleTime: 5_000,
  });

export const useCreateDoctorAccountControlRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorAccountControlApi.create,
    onSuccess: request => {
      syncDoctorControlCaches(queryClient, request);
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
      });
    },
  });
};

export const useApproveDoctorAccountControlRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorAccountControlApi.approve,
    onSuccess: request => {
      syncDoctorControlCaches(queryClient, request);
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
      });
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorInbox(),
      });
    },
  });
};

export const useDenyDoctorAccountControlRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorAccountControlApi.deny,
    onSuccess: request => {
      syncDoctorControlCaches(queryClient, request);
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
      });
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorInbox(),
      });
    },
  });
};

export const useStartDoctorAccountControl = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorAccountControlApi.start,
    onSuccess: request => {
      syncDoctorControlCaches(queryClient, request);
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
      });
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorInbox(),
      });
    },
  });
};

export const useEndDoctorAccountControl = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorAccountControlApi.end,
    onSuccess: request => {
      syncDoctorControlCaches(queryClient, request);
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
      });
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorInbox(),
      });
    },
  });
};

export const useRevokeDoctorAccountControl = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorAccountControlApi.revoke,
    onSuccess: request => {
      syncDoctorControlCaches(queryClient, request);
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
      });
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorInbox(),
      });
    },
  });
};
